/**
 * useA2HSNudge.ts
 *
 * Add-to-Home-Screen nudge — device-local state only.
 *
 * Why localStorage and not Firestore:
 *   A2HS is browser/device specific. Dismissing on desktop should not
 *   suppress on mobile. Firestore would make it incorrectly global.
 *
 * Rules:
 *   - Show once ever on this device/browser after dismiss
 *   - Never show if app is already running in standalone (PWA) mode
 *   - Never show in demo mode
 *   - Dismiss survives navigation and refresh on this device
 *   - No Firestore dependency
 *
 * Target: src/hooks/useA2HSNudge.ts
 */

import { useState } from 'react';

const DISMISSED_KEY = 'coretrak_a2hs_dismissed_v1';

function isStandalone(): boolean {
  try {
    return (
      window.matchMedia('(display-mode: standalone)').matches ||
      ('standalone' in window.navigator && (window.navigator as { standalone?: boolean }).standalone === true)
    );
  } catch {
    return false;
  }
}

function isDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISSED_KEY) === 'true';
  } catch {
    return true; // safe default — do not show if storage unavailable
  }
}

interface UseA2HSNudgeResult {
  isOpen: boolean;
  dismiss: () => void;
}

/**
 * @param eligible - Caller-supplied gate (e.g. onboardingComplete && !isDemoMode).
 *                   Nudge will not open unless this is true.
 */
export function useA2HSNudge(eligible: boolean): UseA2HSNudgeResult {
  const [dismissed, setDismissed] = useState(() => isDismissed() || isStandalone());

  if (!eligible || dismissed) {
    return { isOpen: false, dismiss: () => {} };
  }

  return {
    isOpen: true,
    dismiss: () => {
      try {
        localStorage.setItem(DISMISSED_KEY, 'true');
      } catch { /* ignore */ }
      setDismissed(true);
    },
  };
}
